<!-- <svelte:options strictprops={false} /> -->

<script>
  import { navigate } from "svelte-routing";
  import { onMount, setContext } from "svelte";
  import { writable } from "svelte/store";
  import { Modal } from "svelte-utilities";

  import Navbar from "#lib/Navbar.svelte";
  import OrgSettings from "#lib/settings/OrgSettings.svelte";
  import Datatable from "#lib/datatable/Datatable.svelte";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import BooleanInput from "#lib/sidebar/BooleanInput.svelte";
  import CopyTextField from "#lib/sidebar/CopyTextField.svelte";

  import { orgs } from "#src/stores/organizations.js";
  import { profile } from "#src/stores/auth.js";
  import { createOrg } from "#src/stores/org.js";
  import { getProductLists } from "#src/extensions/supplier-product-data.js";
  import { createStripeCheckoutSession } from "#src/api/stripe.js";
  import { alerts } from "#src/stores/alerts";

  import formatDateTime from "@local/extensions/formatters/format-date-time.js";

  export let orgid;

  let newUser = null;
  let stripeModal;
  let stripeUser = "";
  let stripeEmail = "";
  let createUserAccount = false;
  let stripeLink = "";

  const org = createOrg();
  const productLists = writable([]);

  setContext("productLists", productLists);

  $: $orgs[orgid] && org.init($orgs[orgid]);
  $: capabilities = $profile?.profile_capabilities?.capabilities || {};
  $: userProps = [
    {
      label: "",
      prop: "user_color",
      type: "color",
      editable: false,
    },
    {
      label: "Name",
      prop: "username",
      type: "text",
    },
    {
      label: "Email",
      prop: "email",
      type: "text",
    },
    {
      label: "Role",
      prop: "user_role",
      type: "text",
      editable: false,
    },
    {
      label: "Confirmed",
      prop: "confirmed_at",
      formatter: (v) => (v ? "✓" : ""),
      editable: false,
    },
    ...($org?.org_type === "supplier"
      ? [
          {
            label: "Public",
            prop: "is_public",
            type: "boolean",
          },
        ]
      : []),
    {
      label: "Deactivated",
      prop: "disabled_at",
      formatter: (v) => (v ? "✓" : ""),
      editable: false,
    },
    {
      label: "Last sign in",
      prop: "last_sign_in_at",
      formatter: formatDateTime,
      editable: false,
    },
  ];
  $: stripeButtonDisabled = isStripeButtonDisabled(stripeEmail, createUserAccount);

  function isStripeButtonDisabled(stripeEmail, createUserAccount) {
    if (!createUserAccount) return false;
    return !stripeEmail;
  }

  function clickUser(evt) {
    const { id } = evt.detail;
    navigate(`/profiles/${id}`);
  }

  function addNewUser(evt) {
    const { row } = evt.detail;
    org.addUser({
      ...row,
      send_magic_link: true,
    });
    newUser = null;
  }

  function stopAddingUser() {
    newUser = null;
  }

  function removeUser(evt) {
    const { id } = evt.detail;
    org.removeUser(id);
  }

  async function createStripeLink() {
    const session = await createStripeCheckoutSession({
      price: "price_1RtssEK9GO8MUKURufZEYxXm",
      // price: "price_1SSkE9KCaJ5iM96widUihRkv",
      quantity: 1,
      success_url: "onboard-success",
      cancel_url: "onboard-cancel",
      metadata: {
        onboard_type: "guided-onboard",
        org_id: orgid,
        user_name: stripeUser,
        user_email: stripeEmail,
        create_user_account: createUserAccount,
      },
    });

    if (session.error) {
      alerts.add({ type: "error", message: "Failed to create Stripe link." });
      console.log(session.error);
    } else {
      stripeLink = session.url;
    }
  }

  onMount(async () => {
    orgs.fetchOne(orgid);
    const pl = await getProductLists(orgid);
    productLists.set(pl);
  });
</script>

<Navbar />
{#if $org}
  <div class="grow overflow-auto bg-gray-100 p-4">
    <OrgSettings {org} />
    <h3 class="py-1 px-2">Organization members</h3>
    <div class="bg-white border rounded-lg overflow-hidden">
      <Datatable
        data={$org.profiles}
        columns={userProps}
        newRow={newUser}
        deletable
        clickable
        on:clickrow={clickUser}
        on:addrow={addNewUser}
        on:deleterow={removeUser}
        on:stopaddingrow={stopAddingUser} />
    </div>
  </div>

  <Modal bind:this={stripeModal} closeable>
    <div slot="title">Create Stripe Link</div>
    <div slot="content" class="space-y-4">
      <BooleanInput label="Create User Account on Subscribe" bind:value={createUserAccount} border />
      {#if createUserAccount}
        <TextInput label="User Email" bind:value={stripeEmail} border />
        <TextInput label="User Name" bind:value={stripeUser} border />
      {/if}
      {#if stripeLink}
        <CopyTextField label="Link" value={stripeLink} border />
        <button
          class="btn btn-primary w-full"
          on:click={() => {
            stripeLink = "";
            stripeModal.close();
          }}>Done</button>
      {:else}
        <button class="btn btn-primary w-full" on:click={createStripeLink} disabled={stripeButtonDisabled}
          >Create Link</button>
      {/if}
    </div>
  </Modal>
{/if}
